import { Checkbox, Typography } from "@commonlibs";
import clsx from "clsx";
import Callout from "ui/common/components/feedback/Callout/Callout";
import TabsModalFooter from "ui/common/components/feedback/TabsModal/TabsModalFooter/TabsModalFooter";
import useTranslate from "ui/locale/hooks/useTranslate";
import Form from "ui/common/components/input/Form/Form";
import useTabsState from "ui/common/components/feedback/TabsModal/hooks/useTabsState";
import { UpdateCaducityNames, UpdateCaducityState } from "./UpdateCaducityModal.types";
import useCloseTabModal from "ui/common/components/feedback/TabsModal/hooks/useCloseTabModal";
import useUpdateCaducity from "features/points/hooks/useUpdateCaducity";
import useVerifyReleaseStrategyMethod from "ui/release-strategy/hooks/useVerifyReleaseStrategyMethod";
import { Operations } from "ui/admins/operations.types";
import { NumericTextFieldRoot } from "./UpdateCaducityModal.styles";

export interface ConfirmUpdateCaducityTabProps {
    className?: string;
    style?: React.CSSProperties;
}

export default function ConfirmUpdateCaducityTab({ className, ...rest }: Readonly<ConfirmUpdateCaducityTabProps>) {
    const translate = useTranslate();
    const closeModal = useCloseTabModal();
    const [{ caducity: currentCaducity }] = useTabsState<UpdateCaducityState>();

    const { mutate, isLoading } = useUpdateCaducity({ onSettled: closeModal, onSuccess: handleOnSuccess });

    const { verifyReleaseMethod, isValidatingReleaseStrategy } = useVerifyReleaseStrategyMethod({
        onSuccess: handleOnSuccess,
        isLoading,
    });

    function handleOnSuccess() {
        closeModal();
    }

    function handleSubmit({ caducity }: UpdateCaducityState) {
        const executeAction = (...args: unknown[]) => {
            const [caducityData] = args as [{ caducityDays: number }];
            mutate(caducityData);
        };

        verifyReleaseMethod({
            originalFn: executeAction,
            originalArgs: [{ caducityDays: caducity }],
            operationTypeId: Operations.POINTS_CADUCITY_UPDATE,
        });
    }

    return (
        <Form className={clsx("update-caducity-tab", className)} onSubmit={handleSubmit} {...rest}>
            <Callout status="warning" text={translate("updateCaducityAlert")} />
            <NumericTextFieldRoot
                label={translate("currentCaducity")}
                defaultValue={Number(currentCaducity).toString()}
                maxDecimals={0}
                readonly
            />
            <NumericTextFieldRoot
                name={UpdateCaducityNames.CADUCITY}
                label={translate("newCaducity")}
                placeholder={translate("introduceCaducityDays")}
                defaultValue={Number(currentCaducity).toString()}
                validators={{ gt: 0 }}
                maxDecimals={0}
                required
            />
            <Checkbox
                name="accepted"
                required
                label={<Typography variant="body3Strong">{translate("understandUpdateCaducityImplications")}</Typography>}
            />
            <TabsModalFooter mainType="submit" mainLabel={translate("confirm")} isLoading={isLoading || isValidatingReleaseStrategy} />
        </Form>
    );
}
